/**
 * AP-773 (R6) — bringing a step's DICOM and frame on screen before the
 * change is drawn.
 *
 * Two registries, both module-level so steps never hold React state:
 *  - each viewer pane reports what it currently shows (`registerPaneLocation`);
 *  - the viewer provider registers how to move (`registerViewerNavigator`).
 *
 * Resolution order (spec R6, decision 2026-09-08):
 *  (a) some pane already shows the DICOM → that pane (the step's own pane if
 *      it is one of them) seeks to the frame; nothing else moves;
 *  (b) otherwise → the same jump a worksheet measurement-label click performs
 *      (the DICOM re-seats into the first free pane; free panes page as one
 *      window, pinned panes stay).
 * A pane already at the DICOM and frame → nothing moves.
 *
 * The step's change is applied only once a pane reports the target on screen
 * (`onShown`), so the reader sees the shift, then the revert. If a pane
 * reports that it failed to load the target DICOM, or nothing reports it
 * within SHOW_TIMEOUT_MS, the change is applied anyway with a warning (R19).
 *
 * At most one apply is ever pending. The history flushes it before running
 * the next step (`flushPendingShow`), so a deferred apply can never land
 * after a later undo / redo and overwrite it (I3, linear history).
 */
import type { HistoryLocation } from './historyStore';

export interface PaneLocation {
  dicomUuid: string;
  /** 0-based frame index, as the viewer pane tracks it. */
  frame: number;
}

export interface ViewerNavigator {
  /** The worksheet's label-click jump. `frameNumber` is 1-based. */
  goToAnnotation: (dicomUuid: string, frameNumber: number) => void;
  /** Seek one pane to a frame without re-seating anything. 1-based. */
  seekFrame: (viewportId: string, frameNumber: number) => void;
}

export const SHOW_TIMEOUT_MS = 5000;

const panes = new Map<string, PaneLocation>();
const paneListeners = new Set<() => void>();
const failureListeners = new Set<(dicomUuid: string) => void>();
let navigator: ViewerNavigator | null = null;
/** The apply still waiting for a pane report, if any. */
let pendingFinish: (() => void) | null = null;
const idleListeners = new Set<() => void>();

/** True while a step's apply is still waiting for the viewer. */
export function hasPendingShow(): boolean {
  return pendingFinish !== null;
}

/** Called each time a pending apply has landed (the viewer is idle again).
 *  The history drains queued undo / redo presses from here. */
export function onShowIdle(listener: () => void): () => void {
  idleListeners.add(listener);
  return () => idleListeners.delete(listener);
}

/** Apply a deferred step change right away (skipping the wait), if one is
 *  pending. */
export function flushPendingShow(): void {
  pendingFinish?.();
}

export function registerPaneLocation(
  viewportId: string,
  location: PaneLocation
): void {
  panes.set(viewportId, location);
  paneListeners.forEach((l) => l());
}

export function unregisterPane(viewportId: string): void {
  panes.delete(viewportId);
}

/** R19: a pane could not load `dicomUuid` — a pending step waiting for it
 *  applies right away instead of running out the SHOW_TIMEOUT_MS valve. */
export function reportPaneLoadFailure(
  viewportId: string,
  dicomUuid: string
): void {
  panes.delete(viewportId);
  [...failureListeners].forEach((l) => l(dicomUuid));
}

export function registerViewerNavigator(next: ViewerNavigator): () => void {
  navigator = next;
  return () => {
    if (navigator === next) navigator = null;
  };
}

/** Test/reset hook — history is study-scoped, panes are page-scoped. */
export function resetViewerNavigation(): void {
  panes.clear();
  paneListeners.clear();
  failureListeners.clear();
  idleListeners.clear();
  navigator = null;
  pendingFinish = null;
}

/** The pane currently showing `dicomUuid`, if any. */
export function findPaneShowing(dicomUuid: string): string | null {
  const entry = [...panes.entries()].find(([, l]) => l.dicomUuid === dicomUuid);
  return entry ? entry[0] : null;
}

function paneShowing(dicomUuid: string, preferred: string): string | null {
  const own = panes.get(preferred);
  if (own?.dicomUuid === dicomUuid) return preferred;
  const entry = [...panes.entries()].find(([, l]) => l.dicomUuid === dicomUuid);
  return entry ? entry[0] : null;
}

function isOnScreen(location: HistoryLocation): boolean {
  return [...panes.values()].some(
    (l) => l.dicomUuid === location.dicomUuid && l.frame === location.frame
  );
}

/**
 * Bring `location` on screen per R6, then run `onShown`. Runs it right away
 * when nothing has to move (or there is no location / navigator to move with).
 */
/** How often a pending apply re-checks its `ready` predicate. */
export const READY_POLL_MS = 100;

/**
 * Bring `location` on screen per R6, then run `onShown`. Runs it right away
 * when nothing has to move (or there is no location / navigator to move with).
 *
 * `ready` lets a step wait a little longer after the pane reports: leaving a
 * DICOM unloads its hydrated annotations and returning re-creates them
 * asynchronously, so an annotation step waits until its target exists again
 * (bounded by the same SHOW_TIMEOUT_MS valve).
 */
export function showStepLocation(
  location: HistoryLocation | undefined,
  onShown: () => void,
  ready?: () => boolean
): void {
  // Never let two applies wait at once: the earlier one goes first.
  flushPendingShow();
  const isReady = () => (ready ? ready() : true);
  if (!location || !navigator) {
    onShown();
    return;
  }
  if (isOnScreen(location) && isReady()) {
    onShown();
    return;
  }

  if (!isOnScreen(location)) {
    const frameNumber = location.frame + 1;
    const paneId = paneShowing(location.dicomUuid, location.viewportId);
    if (paneId) {
      navigator.seekFrame(paneId, frameNumber);
    } else {
      navigator.goToAnnotation(location.dicomUuid, frameNumber);
    }
  }

  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let poll: ReturnType<typeof setInterval> | undefined;
  let onPane: (() => void) | undefined;
  let onFailure: ((dicomUuid: string) => void) | undefined;
  const finish = () => {
    if (done) return;
    done = true;
    if (pendingFinish === finish) pendingFinish = null;
    if (onPane) paneListeners.delete(onPane);
    if (onFailure) failureListeners.delete(onFailure);
    clearTimeout(timer);
    clearInterval(poll);
    onShown();
    [...idleListeners].forEach((l) => l());
  };
  pendingFinish = finish;
  const check = () => {
    if (isOnScreen(location) && isReady()) finish();
  };
  onPane = check;
  if (ready) poll = setInterval(check, READY_POLL_MS);
  onFailure = (dicomUuid) => {
    if (dicomUuid !== location.dicomUuid) return;
    // eslint-disable-next-line no-console -- R19: the DICOM failed to load; apply anyway.
    console.warn(
      '[AP-773] target DICOM failed to load; applying the step anyway',
      location
    );
    finish();
  };
  timer = setTimeout(() => {
    // eslint-disable-next-line no-console -- R19: the DICOM never showed; apply anyway.
    console.warn(
      '[AP-773] target DICOM/frame (or its annotation) not shown within 5 s; applying the step anyway',
      location
    );
    finish();
  }, SHOW_TIMEOUT_MS);
  paneListeners.add(onPane);
  failureListeners.add(onFailure);
}
